import { useEffect, useMemo, useRef, useState } from 'react';
import type { Order, OrderItem, OrderStatus } from '../lib/database.types';
import {
  loadOrders,
  subscribeOrders,
  statusLabel,
  paymentStatusLabel,
  sortByNewest,
  NEXT_STATUS,
  updateOrderStatus,
  markOrderPaid,
  isAwaitingOnlinePayment,
  isKitchenVisible,
  isUberOrder,
  orderSourceBadge,
} from '../lib/orders';
import { formatPrice } from '../lib/cart';
import { buildTicketModel, printKitchenTicket, shouldAutoPrint, shortTime, cleanProductName } from '../lib/ticket';
import { subscribeTabletPush, tabletPushActive, pushSupported } from '../lib/tabletPush';
import { addManualStamp } from '../lib/loyaltyClient';
import { setTenantOpen } from '../lib/tenantSettingsService';
import { shouldRingForMessage } from '../lib/tabletAlarm';
import { playOrderAlert, stopOrderAlert } from '../lib/orderSiren';
import { startTabletHeartbeat } from '../lib/tabletHeartbeat';

/**
 * Mode tablette / caisse (parité legacy TabletView) : tableau de bord cuisine plein écran.
 * Horloge live, son, KPIs (commandes du jour, CA encaissé, en cuisine, à retirer, à encaisser),
 * onglets (en cours / à encaisser / historique), workflow 1 clic (accepter → cuisine → prête →
 * récupérée), impression ticket + impression auto. Données temps réel Supabase.
 */
interface OrderListProps {
  tenantId: string;
  currency?: 'EUR' | 'CHF';
  tenantName?: string;
  tenantSlug?: string;
  /** Entitlement `loyalty` effectif : affiche le tampon fidélité caisse. Défaut true. */
  loyaltyEnabled?: boolean;
}

const AUTOPRINT_KEY = 'lilco_tablet_autoprint';
const SOUND_KEY = 'lilco_tablet_sound';
const OPEN_KEY = 'lilco_tablet_open';

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

/** Alarme « nouvelle commande » : sirène stridente 5 s (reprend toutes les 10 s tant que non traitée). */
function playAlarm() {
  playOrderAlert({ duration: 5000 });
}

export default function OrderList({ tenantId, currency = 'EUR', tenantName, tenantSlug, loyaltyEnabled = true }: OrderListProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'current' | 'cash' | 'history'>('current');
  const [now, setNow] = useState(() => new Date());
  const [autoPrint, setAutoPrint] = useState<boolean>(() => typeof localStorage !== 'undefined' && localStorage.getItem(AUTOPRINT_KEY) === '1');
  const [soundOn, setSoundOn] = useState<boolean>(() => typeof localStorage === 'undefined' || localStorage.getItem(SOUND_KEY) !== '0');
  const [isOpen, setIsOpen] = useState<boolean>(() => typeof localStorage === 'undefined' || localStorage.getItem(OPEN_KEY) !== '0');
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const printedRef = useRef<Set<string>>(new Set());

  // Horloge live
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Heartbeat tablette (Chantier 2) : signale que cet écran cuisine est vivant.
  useEffect(() => startTabletHeartbeat(tenantId), [tenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { orders: list, itemsByOrder: items } = await loadOrders(tenantId);
      if (cancelled) return;
      setOrders(sortByNewest(list));
      setItemsByOrder(items);
      setLoading(false);
    })();

    const sub = subscribeOrders(tenantId, {
      onInsert: (o) => {
        setOrders((prev) => (prev.some((p) => p.id === o.id) ? prev : sortByNewest([o, ...prev])));
        // Sonnerie : gérée par l'effet « alarme répétée » (5 s + toutes les 10 s tant que non traitée).
        loadOrders(tenantId).then(({ itemsByOrder: items }) => {
          setItemsByOrder((prev) => ({ ...prev, ...items }));
          // Les commandes Uber sont déjà imprimées côté Uber -> pas d'auto-impression (anti-doublon).
          if (autoPrint && shouldAutoPrint(o.status) && !isAwaitingOnlinePayment(o) && !isUberOrder(o) && !printedRef.current.has(o.id)) {
            printKitchenTicket(buildTicketModel(o, items[o.id] ?? [], { tenantName, currency }));
            printedRef.current.add(o.id);
          }
        });
      },
      onUpdate: (o) => {
        // MàJ locale ; la sonnerie (y compris quand un paiement en ligne passe « payé »
        // et fait entrer la commande en file) est gérée par l'effet « alarme répétée ».
        setOrders((prev) => prev.map((p) => (p.id === o.id ? { ...p, ...o } : p)));
      },
    });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, autoPrint]);

  const toggleAuto = () => setAutoPrint((v) => { const n = !v; if (typeof localStorage !== 'undefined') localStorage.setItem(AUTOPRINT_KEY, n ? '1' : '0'); return n; });
  const toggleSound = () => setSoundOn((v) => {
    const n = !v;
    // Turning ON : court test de sirène (vérifie le son ET débloque l'AudioContext du navigateur).
    if (n) playOrderAlert({ duration: 1500 });
    else stopOrderAlert();
    if (typeof localStorage !== 'undefined') localStorage.setItem(SOUND_KEY, n ? '1' : '0');
    return n;
  });

  // ALARME RÉPÉTÉE (parité comptoir) : tant qu'au moins une commande « reçue » n'est
  // NI acceptée NI refusée, on sonne 5 s, puis on RE-sonne toutes les 10 s. Dès que
  // toutes les commandes sont traitées (ou son coupé) → on arrête.
  const pendingCount = orders.filter((o) => o.status === 'recue' && !isAwaitingOnlinePayment(o)).length;
  useEffect(() => {
    if (!soundOn || pendingCount === 0) {
      stopOrderAlert();
      return;
    }
    playOrderAlert({ duration: 5000 }); // sonne 5 s tout de suite
    const id = setInterval(() => playOrderAlert({ duration: 5000 }), 10000); // puis toutes les 10 s
    return () => clearInterval(id);
  }, [pendingCount, soundOn]);

  // Notifications push de la tablette (son cuisine fiable même écran éteint).
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { tabletPushActive().then(setPushOn); }, []);

  // Réveil de la caisse par le service worker (push « nouvelle commande ») : joue
  // l'alarme même si le Realtime est tombé — déclenché côté serveur via order-notify.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMsg = (e: MessageEvent) => {
      if (shouldRingForMessage(e.data, soundOn)) playAlarm();
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [soundOn]);
  const enablePush = async () => {
    if (!tenantSlug) return;
    setPushBusy(true);
    const r = await subscribeTabletPush(tenantSlug);
    setPushBusy(false);
    setPushOn(r === 'subscribed');
    if (r === 'unsupported') alert('Les notifications ne sont pas supportées sur ce navigateur. Utilisez Chrome/Edge sur la tablette.');
    else if (r === 'denied') alert('Autorisez les notifications dans le navigateur pour activer le son cuisine fiable (même écran éteint).');
    else if (r === 'error') alert('Activation impossible pour le moment. Réessayez.');
  };
  const toggleOpen = () => setIsOpen((v) => { const n = !v; if (typeof localStorage !== 'undefined') localStorage.setItem(OPEN_KEY, n ? '1' : '0'); setTenantOpen(tenantId, n); return n; });
  const cashIn = (o: Order) => { setOrders((prev) => prev.map((p) => (p.id === o.id ? { ...p, payment_status: 'onsite_collected' } : p))); markOrderPaid(o.id); };

  const advance = (o: Order, status: OrderStatus) => {
    setOrders((prev) => prev.map((p) => (p.id === o.id ? { ...p, status } : p)));
    updateOrderStatus(o.id, status);
  };
  const print = (o: Order) => printKitchenTicket(buildTicketModel(o, itemsByOrder[o.id] ?? [], { tenantName, currency }));

  const kpis = useMemo(() => {
    const today = orders.filter((o) => isToday(o.created_at));
    const paidStatuses = new Set(['paid', 'onsite_collected']);
    return {
      ordersToday: today.length,
      caEncaisse: today.filter((o) => paidStatuses.has(o.payment_status ?? '')).reduce((s, o) => s + o.total_cents, 0),
      enCuisine: orders.filter((o) => o.status === 'en_cuisine').length,
      aRetirer: orders.filter((o) => o.status === 'prete').length,
      aEncaisser: orders.filter((o) => o.payment_status === 'onsite_pending' && o.status !== 'refusee' && o.status !== 'annulee'),
    };
  }, [orders]);

  // File de préparation : commandes actives TRIÉES par heure de retrait (ordre de sortie),
  // pour que la cuisine prépare dans le bon ordre. On en déduit un n° de passage (rank).
  const pickupKey = (o: Order) => `${o.pickup_date ?? '9999-12-31'}T${o.pickup_time ?? '23:59:59'}`;
  const currentOrders = useMemo(
    () =>
      orders
        // Parité legacy (BB/FK) : file cuisine = commandes actives, paniers abandonnés
        // (online non payé) exclus (cf. isKitchenVisible, testé).
        .filter(isKitchenVisible)
        .sort((a, b) => pickupKey(a).localeCompare(pickupKey(b))),
    [orders],
  );
  const rankById = useMemo(() => {
    const m: Record<string, number> = {};
    currentOrders.forEach((o, i) => (m[o.id] = i + 1));
    return m;
  }, [currentOrders]);
  const historyOrders = orders.filter((o) => ['recuperee', 'refusee', 'annulee'].includes(o.status));
  const shown = tab === 'current' ? currentOrders : tab === 'cash' ? kpis.aEncaisser : historyOrders;

  if (loading) return <p className="p-6 text-slate-300">Chargement des commandes…</p>;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* En-tête caisse */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-700 bg-slate-950 px-5 py-3">
        <div>
          <h1 className="text-xl font-extrabold">{tenantName ?? 'Restaurant'} — Tablette</h1>
          <p className="text-xs text-slate-400">Process simplifié · 1 clic = prêt · 1 clic = récupéré</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-right">
            <div className="text-[10px] uppercase text-slate-400">Heure</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{now.toLocaleTimeString('fr-FR')}</div>
          </div>
          <button onClick={toggleOpen} className={'rounded-lg px-3 py-2 text-sm font-bold ' + (isOpen ? 'bg-emerald-600' : 'bg-red-700')}>
            {isOpen ? '✅ Ouvert' : '⛔ Fermé'}
          </button>
          <button onClick={toggleSound} className={'rounded-lg px-3 py-2 text-sm font-bold ' + (soundOn ? 'bg-emerald-600' : 'bg-slate-700 text-slate-300')}>
            {soundOn ? '🔔 Son ON' : '🔕 Son OFF'}
          </button>
          {pushSupported() && (
            <button
              onClick={enablePush}
              disabled={pushBusy || pushOn}
              title="Notification même écran éteint / onglet en arrière-plan (fiable)"
              className={'rounded-lg px-3 py-2 text-sm font-bold ' + (pushOn ? 'bg-emerald-600' : 'bg-amber-500 text-white')}
            >
              {pushBusy ? '⏳…' : pushOn ? '🔔 Notif. ON' : '🔔 Activer notif.'}
            </button>
          )}
          <button onClick={toggleAuto} className={'rounded-lg px-3 py-2 text-sm font-bold ' + (autoPrint ? 'bg-emerald-600' : 'bg-slate-700 text-slate-300')}>
            🖨️ Auto {autoPrint ? 'ON' : 'OFF'}
          </button>
          {loyaltyEnabled && (
            <button onClick={() => setLoyaltyOpen(true)} className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-white hover:bg-amber-600">
              🎴 Fidélité
            </button>
          )}
          {tenantSlug && (
            <a href={`/r/${tenantSlug}/admin`} className="rounded-lg bg-slate-700 px-3 py-2 text-sm font-bold text-slate-100 hover:bg-slate-600">
              ⚙️ Admin
            </a>
          )}
        </div>
      </header>

      {loyaltyOpen && (
        <LoyaltyStampModal tenantId={tenantId} currency={currency} onClose={() => setLoyaltyOpen(false)} />
      )}

      <div className="p-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Kpi label="📋 Commandes du jour" value={String(kpis.ordersToday)} />
          <Kpi label="💰 CA encaissé" value={formatPrice(kpis.caEncaisse, currency)} />
          <Kpi label="🔥 En cuisine" value={String(kpis.enCuisine)} tone="border-red-800" />
          <Kpi label="📦 À retirer" value={String(kpis.aRetirer)} tone="border-emerald-800" />
          <Kpi label="💵 À encaisser" value={formatPrice(kpis.aEncaisser.reduce((s, o) => s + o.total_cents, 0), currency)} sub={`${kpis.aEncaisser.length} commande${kpis.aEncaisser.length > 1 ? 's' : ''}`} />
        </div>

        {/* Onglets */}
        <div className="mt-5 flex gap-1 border-b border-slate-700">
          {([['current', '🍳 Commandes en cours'], ['cash', '💵 À encaisser sur place'], ['history', '📚 Historique']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={'border-b-2 px-4 py-2.5 text-sm font-semibold -mb-px ' + (tab === id ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200')}>
              {label}
            </button>
          ))}
        </div>

        {/* Liste */}
        {shown.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <div className="text-5xl">😴</div>
            <div className="mt-2 text-lg font-semibold">Aucune commande {tab === 'current' ? 'en cours' : tab === 'cash' ? 'à encaisser' : 'dans l’historique'}</div>
            <div className="text-sm">Les nouvelles arrivent ici automatiquement.</div>
          </div>
        ) : (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {shown.map((o) => {
              const items = itemsByOrder[o.id] ?? [];
              const point = (o as { order_point?: string | null }).order_point;
              const next = NEXT_STATUS[o.status];
              const rank = tab === 'current' ? rankById[o.id] : undefined;
              const pickup = shortTime(o.pickup_time);
              return (
                <div key={o.id} className="rounded-xl border border-slate-700 bg-slate-800 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      {rank != null && (
                        <div className="flex flex-shrink-0 flex-col items-center justify-center rounded-lg bg-amber-500 px-2.5 py-1 text-slate-950">
                          <span className="text-[9px] font-bold uppercase leading-none">Sortie</span>
                          <span className="text-xl font-extrabold leading-tight">{rank}</span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${orderSourceBadge(o, tenantName).cls}`}>
                            {orderSourceBadge(o, tenantName).label}
                          </span>
                          <span className="font-bold">#{o.order_number ?? o.id.slice(0, 6)} · {o.customer_name ?? 'Client'}</span>
                        </div>
                        <div className="text-sm text-slate-400">
                          {statusLabel(o.status)} · {o.pickup_mode === 'delivery' ? 'Livraison' : 'Retrait'} · {paymentStatusLabel(o.payment_status)}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {pickup && (
                            <span className="inline-flex items-center gap-1 rounded bg-slate-700 px-2 py-0.5 text-xs font-bold text-amber-300">
                              ⏱ {o.pickup_mode === 'delivery' ? 'Livraison' : 'Retrait'} {pickup}
                            </span>
                          )}
                          {point && <span className="inline-block rounded bg-slate-700 px-2 py-0.5 text-xs font-semibold">📍 {point}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="font-bold">{formatPrice(o.total_cents, currency)}</span>
                      <button onClick={() => print(o)} className="rounded border border-slate-600 px-2 py-0.5 text-xs hover:bg-slate-700">🖨️ Imprimer</button>
                    </div>
                  </div>

                  {items.length > 0 && (
                    <ul className="mt-2 border-t border-slate-700 pt-2 text-sm">
                      {items.map((it) => (
                        <li key={it.id} className="py-0.5">
                          <div className="flex gap-2"><span className="font-bold">{it.quantity}×</span><span>{(it.options && it.options.length) ? cleanProductName(it.name) : it.name}</span></div>
                          {(it.options ?? []).map((o, oi) => (
                            <div key={oi} className="pl-5 text-xs text-slate-400">› {o.group}: {o.values.join(', ')}</div>
                          ))}
                        </li>
                      ))}
                    </ul>
                  )}
                  {o.customer_note && <div className="mt-2 rounded bg-amber-900/40 px-2 py-1 text-sm text-amber-200">📝 {o.customer_note}</div>}

                  {/* Actions 1 clic */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {isAwaitingOnlinePayment(o) ? (
                      <div className="w-full rounded-lg border-2 border-amber-600 bg-amber-900/40 px-3 py-2 text-center text-sm font-bold text-amber-200">
                        ⏳ En attente du paiement en ligne du client — ne pas préparer
                      </div>
                    ) : (
                      <>
                        {o.status === 'recue' && (
                          <>
                            <button onClick={() => advance(o, 'acceptee')} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-bold hover:bg-emerald-700">✓ Accepter</button>
                            <button onClick={() => advance(o, 'refusee')} className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-bold hover:bg-red-800">✕ Refuser</button>
                          </>
                        )}
                        {next && (
                          <button onClick={() => advance(o, next.status)} className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-bold hover:bg-amber-700">{next.label}</button>
                        )}
                        {o.payment_status === 'onsite_pending' && o.status !== 'refusee' && o.status !== 'annulee' && (
                          <button onClick={() => cashIn(o)} className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-bold hover:bg-green-700">💵 Marquer encaissé</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className={'rounded-xl border bg-slate-800 p-3 ' + (tone ?? 'border-slate-700')}>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-extrabold">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

/** Modale caisse fidélité : ajoute un tampon manuel à un client (paiement cash au comptoir). */
function LoyaltyStampModal({ tenantId, currency, onClose }: { tenantId: string; currency: 'EUR' | 'CHF'; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const cents = Math.round(parseFloat(amount.replace(',', '.') || '0') * 100);
    const r = await addManualStamp(email, tenantId, cents, firstName);
    setBusy(false);
    setResult(r);
    if (r.ok) {
      setEmail('');
      setAmount('');
      setFirstName('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 text-slate-900">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-amber-600">🎴 Ajouter un tampon fidélité</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="mb-3 text-sm text-slate-500">Client réglant au comptoir : saisissez son email et le montant payé.</p>
        <label className="mb-2 block text-sm">
          <span className="mb-1 block text-xs font-semibold opacity-60">Email client</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className="w-full rounded-lg border border-slate-300 p-2.5" />
        </label>
        <div className="mb-2 grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold opacity-60">Montant payé ({currency === 'CHF' ? 'CHF' : '€'})</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="12.50" className="w-full rounded-lg border border-slate-300 p-2.5" />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold opacity-60">Prénom (optionnel)</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full rounded-lg border border-slate-300 p-2.5" />
          </label>
        </div>
        {result && <p className={'mb-2 text-sm font-semibold ' + (result.ok ? 'text-emerald-600' : 'text-red-600')}>{result.message}</p>}
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-amber-500 py-2.5 font-semibold text-white hover:bg-amber-600 disabled:opacity-40">
          {busy ? 'Ajout…' : '✓ Ajouter le tampon'}
        </button>
      </form>
    </div>
  );
}
